import { useMutation } from '@tanstack/react-query';
import { renderDocxTemplate } from '@dealstack/frontend/shared/dataExchange/services/mip.service.ts';
import { useTranslationDefault } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { isStandardBackendError } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import type { FlowRuleScenario } from '@dealstack/shared/lib/common/business/documentFlow';
import { PreviewRes } from '@dealstack/frontend/shared/components/FieldBinding/fieldBinding.types.ts';

interface Params {
	templateId: string;
	/** Omitted renders the fields view, with each field shown by name in place. */
	investorId?: string;
	/**
	 * The document as it stands in the editor, unsaved edits and all.
	 *
	 * Sent so the preview answers "what will this produce" for what is ON SCREEN. Omitted, the
	 * server renders what is stored — which is right when there is nothing unsaved, and wrong
	 * in the only case the author is actually asking about.
	 */
	document?: unknown;
	bindings?: Record<string, string>;
	/** Hand-set answers for what the rules read. Omitted renders the data as it is. */
	scenario?: FlowRuleScenario;
}

export const useDocxTemplateRender = () => {
	const tDefault = useTranslationDefault();
	const { dealId, mipId } = useRequiredParams('dealId', 'mipId');

	return useMutation({
		mutationFn: async ({
			templateId,
			investorId,
			document,
			bindings,
			scenario,
		}: Params): Promise<PreviewRes> => {
			if (!templateId) {
				throw new Error('Template ID not found');
			}
			return await renderDocxTemplate({
				dealId,
				programmeId: mipId,
				templateId,
				...(investorId === undefined ? {} : { investorId }),
				...(document === undefined ? {} : { document }),
				...(bindings === undefined ? {} : { bindings }),
				...(scenario === undefined ? {} : { scenario }),
			});
		},
		onError: (error: unknown) => {
			if (isStandardBackendError(error)) {
				showToastV2({
					state: 'error',
					title: tDefault(`error_codes.${error.error?.code}`),
				});
				return;
			}
			showToastV2({ state: 'error', title: tDefault('common.toast.error.message') });
		},
	});
};
